import { useMemo } from 'react';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificationService } from '@/api/services/NotificationService';
import { useAuth } from '@/stores/authContext';
import type { NotificationItem } from '@/types/notifications';

export const NOTIFICATIONS_KEY = ['notifications', 'list'] as const;

/** Newest-first по createdDate (строковое ISO сравнимо лексикографически). */
function sortByDateDesc(items: readonly NotificationItem[]): NotificationItem[] {
  return [...items].sort((a, b) => (b.createdDate ?? '').localeCompare(a.createdDate ?? ''));
}

/** Добавить/обновить видимое уведомление из SSE в кэш истории (дедуп по id, newest-first). */
export function upsertNotification(queryClient: QueryClient, item: NotificationItem): void {
  if (!item.id) {
    return;
  }
  queryClient.setQueryData<NotificationItem[]>(NOTIFICATIONS_KEY, (prev) => {
    const rest = (prev ?? []).filter((n) => n.id !== item.id);
    return sortByDateDesc([item, ...rest]);
  });
}

/** История видимых уведомлений (server-state). */
export function useNotificationList() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: ({ signal }) => notificationService.list({ displayable: true }, signal),
    enabled: isAuthenticated,
    staleTime: 60_000,
    select: sortByDateDesc,
  });
}

/** Число непрочитанных (отдельного эндпоинта на бэке нет — считаем из списка). */
export function useUnreadCount(items: readonly NotificationItem[] | undefined): number {
  return useMemo(() => (items ?? []).filter((n) => !n.isRead).length, [items]);
}

function markRead(items: readonly NotificationItem[] | undefined, id?: string): NotificationItem[] {
  return (items ?? []).map((n) => (id == null || n.id === id ? { ...n, isRead: true } : n));
}

/** Пометить одно уведомление прочитанным (оптимистично, с откатом при ошибке). */
export function useReadNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => notificationService.read(id),
    onMutate: (id) => {
      const prev = queryClient.getQueryData<NotificationItem[]>(NOTIFICATIONS_KEY);
      queryClient.setQueryData<NotificationItem[]>(NOTIFICATIONS_KEY, (cur) => markRead(cur, id));
      return { prev };
    },
    onError: (_error, _id, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(NOTIFICATIONS_KEY, ctx.prev);
      }
    },
  });
}

/** Пометить все видимые уведомления прочитанными (оптимистично, с откатом). */
export function useReadAllNotifications() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => notificationService.readAll(),
    onMutate: () => {
      const prev = queryClient.getQueryData<NotificationItem[]>(NOTIFICATIONS_KEY);
      queryClient.setQueryData<NotificationItem[]>(NOTIFICATIONS_KEY, (cur) => markRead(cur));
      return { prev };
    },
    onError: (_error, _vars, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(NOTIFICATIONS_KEY, ctx.prev);
      }
    },
  });
}
